/**
 * Haptics (ui-ux §8.2): `haptic(id)` wraps `navigator.vibrate` with the pattern table, the enable rules, a 50 ms
 * minimum spacing with "stronger wins" collisions, and a 50-entry log for headless tests (`__marg.ui.haptics.log`).
 * Owner: UI/Visual (F3).
 */

export const HAPTICS = {
  select: 10, confirm: 18, seal: 25, deny: [30, 40, 30], longpress: 12, omen_open: 10, omen_perfect: 20, omen_good: 12,
  react_window: 10, hit_taken_heavy: 30, crit: [15, 25, 35], ko: 15, levelup: [30, 60, 30, 60, 90],
  loot_rare: [15, 35, 15], loot_gilded: [20, 40, 20, 40, 60], card_play: 14, error: [50, 40, 50],
} as const satisfies Record<string, number | readonly number[]>;
export type HapticId = keyof typeof HAPTICS;

/** Collision priority (rule 2): `ko` > `levelup` > `crit` > `omen_*` > the rest. */
export function hapticRank(id: HapticId): number {
  if (id === 'ko') return 4;
  if (id === 'levelup') return 3;
  if (id === 'crit') return 2;
  if (id.startsWith('omen_')) return 1;
  return 0;
}

export interface HapticLogEntry { id: HapticId; t: number; fired: boolean; reason?: string }

const log: HapticLogEntry[] = [];
let enabled = true;
let lastAt = -Infinity;
let lastRank = -1;

/** Settings «Вибрация» (`haptics.on`). */
export function setHapticsEnabled(on: boolean): void { enabled = on; }

/** Whether the device can vibrate at all (the settings row is hidden otherwise, rule 4). */
export function hapticsSupported(): boolean {
  return typeof navigator !== 'undefined' && 'vibrate' in navigator && matchMedia('(pointer: coarse)').matches;
}

function push(e: HapticLogEntry): void { log.push(e); if (log.length > 50) log.shift(); }

/** Fires a haptic pattern if allowed (rule 1) and not suppressed by a stronger one within 50 ms (rule 2). */
export function haptic(id: HapticId, now: number = performance.now()): boolean {
  let reason: string | undefined;
  if (!enabled) reason = 'off';
  else if (typeof navigator === 'undefined' || !('vibrate' in navigator)) reason = 'unsupported';
  else if (!navigator.userActivation?.hasBeenActive) reason = 'no-activation';
  else if (document.visibilityState !== 'visible') reason = 'hidden';
  else if (now - lastAt < 50 && hapticRank(id) <= lastRank) reason = 'spacing';
  const fired = !reason;
  if (fired) {
    const p = HAPTICS[id];
    navigator.vibrate(typeof p === 'number' ? p : [...p]);
    lastAt = now; lastRank = hapticRank(id);
  }
  push({ id, t: Math.round(now), fired, reason });
  return fired;
}

/** The ring buffer (last 50 calls, fired or not). */
export function hapticLog(): readonly HapticLogEntry[] { return log; }
